/**
 * The MaskEditor's public TYPE surface, and the one pure helper that belongs with it.
 *
 * Split out of `MaskEditor.tsx` (WS-J J-12, the remainder commit). These 200 lines are the
 * component's CONTRACT with its five importers, not its implementation, and every extracted hook
 * needed `MaskEditorProps` / `MatteKind` — reaching back into `MaskEditor.js` for them made a
 * type-only import cycle out of every one of them. They resolve here instead.
 *
 * `MaskEditor.tsx` re-exports all four names, so nothing outside this directory changed: the
 * surface test (J-1) still finds `MaskEditor`, `defaultBrandRect` and `DEFAULT_PRINT_AREA` on the
 * module its importers actually import.
 */
import type { ReactNode, Ref } from "react";
import type { ImageAsset } from "@genz/platform";
import type { AccordionItem } from "../ui/Accordion.js";
import type { EngineBinding, FitMode, Mask, MockupAssets, Polygon, ShadingParams, VarnishParams } from "@genz/engine-bind";
import type { AppLogger } from "../app/context.js";
import type { ProductCategory } from "./compositing.js";
import { rectFromDiagonal } from "./useMaskGeometry.js";

/**
 * A3 — imperative bake handle. Lets the parent (TemplateEditor.saveDraft) force a
 * bake+upload of the layered-mockup assets before it submits the version, so a Save that
 * follows a bake-relevant appearance change (AI edge, rotation, fill, aspect, texture /
 * clear-bg, layer opacity …) never persists the geometry while leaving the cloud's baked
 * assets STALE — which made other devices render the design-only fallback. Minimal surface:
 * query dirtiness + trigger the bake; the actual cross-device upload (and its non-blocking
 * failure banner) stays owned by the parent's `onBaked` handler.
 */
export interface MaskBakeHandle {
  /** True when a bake-relevant appearance change happened since the last successful
   *  upload-bake (`previewMockup(true)` → `onBaked`). Over-approximates safely: it may be
   *  true right after a reopen-restore, but it is NEVER false while a real change is pending. */
  isDirty(): boolean;
  /** Force an upload-bake (`previewMockup(true)`). Resolves `true` when `onBaked` was emitted
   *  (a payload was handed up for cross-device sync), `false` when nothing could be baked
   *  (e.g. no source canvas / locked). Clears the dirty flag on a successful emit. */
  bakeNow(): Promise<boolean>;
  /** True when this version+active view already has baked mockup assets in the local cache
   *  (an in-session bake, or hydrated cloud assets). Lets Save tell a template that will export
   *  a real mockup from one that would fall back to design-only. */
  hasBake(): boolean;
  /** Idempotent replay of the cached matte from the PRISTINE decoded source (Task 7). Re-keys
   *  the removed image from scratch with the current calibration/params — the foundation the
   *  calibration sliders re-apply on. Returns `true` when it re-applied, `false` when there is
   *  nothing to replay (locked, no removal yet, or no cached matte). The internal calibration
   *  UI calls the component-local `reapplyMatte`; this handle entry lets a parent/test drive it. */
  reapplyMatte(): boolean;
  /** True once the case image decoded into the source canvas. When false a bake is IMPOSSIBLE —
   *  every removal/preview control is disabled — so Save must not tell the operator to press
   *  them, and must not hard-block on a bake that cannot happen (ED-4). */
  sourceReady(): boolean;
  /** True when the case image FAILED to load (404 / CORS / dead host) rather than simply not
   *  having been provided. Lets Save name the real cause and point at Force sync (ED-4). */
  caseImageFailed(): boolean;
}

export interface MaskEditorProps {
  engine: EngineBinding;
  /** A3 — imperative bake handle (see {@link MaskBakeHandle}). Optional; when set, the parent
   *  can force a pending bake+upload before it persists geometry. */
  bakeRef?: Ref<MaskBakeHandle>;
  /** Client observability logger — auto actions log to the ACTIVITY feed (info) and
   *  failures to the ERROR log (error, full detail). Optional for standalone/tests. */
  logger?: AppLogger;
  /** Object URL or data URL for the normalized case image (background). */
  caseImageUrl?: string;
  printArea: Polygon;
  cutouts: Mask[];
  onPrintAreaChange(p: Polygon): void;
  onCutoutsChange(c: Mask[]): void;
  /** Spec 017 US6: optional editable brand-logo placement quad (4 normalized corners).
   *  When provided, it renders as a draggable amber quad; null/undefined hides it. */
  brandArea?: Polygon | null;
  onBrandAreaChange?(p: Polygon): void;
  /** Template VERSION id (spec 019) — when set, "Preview mockup" also BAKES + persists
   *  the layered-mockup assets under this id so the batch render reuses them. */
  versionId?: string;
  /** The geometric view being authored (Task B4 multi-view). The local bake is keyed under
   *  (versionId, view) so a per-view bake doesn't overwrite another view's local mockup.
   *  Defaults to the primary (back) view for single-view callers. */
  view?: string;
  /** Mounted but not on screen (a Generate run's other views in the wizard): it keeps its state
   *  and bakes, but the arrow keys never nudge its geometry. */
  inactive?: boolean;
  /** Template id — when set, an uploaded TEST DESIGN (+ test background) persists with the
   *  template (testDesignStore) so the live mockup preview is reproducible across sessions. */
  templateId?: string;
  /** WS-BG F2 — the template's chosen background colour, sRGB `[r, g, b, a]`, already resolved
   *  from the LIVE Background picker (`resolveBackgroundColor`) and adjusted for the preview's
   *  own format (`backgroundForFormat(…, "png")`). The live mock-up preview composites on it, so
   *  picking "Solid color" red shows red immediately instead of white until the next export.
   *
   *  It is a RENDER SETTING, not a baked asset: it recomposes the preview and never marks the
   *  bake dirty. `undefined`/`null` ⇒ the historical white canvas. */
  templateBackgroundColor?: [number, number, number, number] | null;
  /** The preview's backdrop image, drawn behind the case: the Generate job's clear image, so an
   *  empty print area shows the phone through the clear case (owner, 2026-10-15). Preview only:
   *  never baked, never saved. Absent ⇒ a light grey backdrop. */
  previewBackdropUrl?: string;
  /** Admin-curated image-library options for the product-profile pickers — TEXTURE overlays
   *  + CLEAR-CASE backgrounds. When non-empty, the profile shows a "pick from library"
   *  dropdown next to the custom upload. Optional (standalone/tests → upload only). */
  textureOptions?: ImageAsset[];
  clearBgOptions?: ImageAsset[];
  /** Generate's clear image — the case image's own frame with a clear case, no magenta. It turns
   *  the clear-case layer on and sits 1:1 under the keyed print area. */
  clearCaseImage?: Uint8Array;
  /** Called after a local bake (spec 019) with the baked assets + the product mask as a
   *  grayscale PNG, so the parent can UPLOAD them for cross-device sync. Best-effort. */
  onBaked?(payload: {
    assets: MockupAssets;
    maskPng: Uint8Array;
    printMaskPng?: Uint8Array;
    /** RAW AI-matte encoded as a grayscale PNG (255 = kept object), when a removal produced
     *  one. Persisted so the reopen restore replays it → the removed state + calibration. */
    mattePng?: Uint8Array;
    /** AI-edge toggle + protect-content slider, persisted so they restore on reopen. */
    aiEdge?: boolean;
    aiEdgeKeep?: number;
    /** Per-template edge-calibration sliders (edgeFeather 0..10, edgeChoke −5..10,
     *  matteStrength 0..100), persisted so reopening restores the exact cutout the admin
     *  calibrated. 0/negative are meaningful (a saved 0 must override the global default). */
    edgeFeather?: number;
    edgeChoke?: number;
    matteStrength?: number;
    /** Which technique produced the cutout — `"chroma"` (the Magenta key) makes the reopen
     *  restore RE-KEY the pristine pixels instead of replaying the saved grey matte. */
    matteKind?: MatteKind;
    /** The user DELIBERATELY cleared the AI removal this session ("Restore background"). Tells the
     *  uploader NOT to carry a previously-hosted matte forward on this matte-less bake — the wipe is
     *  intended. (When absent, a matte-less bake carries the existing matte forward so an incidental
     *  re-bake before the async restore finishes can't drop the removal state.) */
    matteCleared?: boolean;
    /** Hosted library URLs of the picked layers (texture / clear-bg) + texture blend +
     *  placement rotation, so the parent persists them to the cloud refs for cross-device
     *  restore. */
    layerRefs?: {
      textureUrl?: string; clearBgUrl?: string; textureBlend?: string; designRotation?: number;
      /** Whether the "Texture / effect layer" was ON for this bake. Emitted SEPARATELY from
       *  `textureUrl` — a UV-varnish layer needs no texture image, so the url can't stand in
       *  for the enable flag (that inference silently lost the layer on reopen). */
      textureOn?: boolean;
      fillFit?: FitMode; designAspect?: [number, number] | null; varnish?: VarnishParams;
      /** Artwork shading strength + per-stage pipeline toggles (AI settings popup), persisted so
       *  a template keeps its calibrated pipeline on reopen. `matteQuality` is the per-removal
       *  model choice (fast/quality), persisted so the NEXT removal on this template defaults to
       *  what the admin last picked (it doesn't reshape an already-baked matte). */
      printShadingStrength?: number; stageDespill?: boolean; stageRefine?: boolean;
      stageMatteStrength?: boolean; stageShading?: boolean; matteQuality?: "fast" | "quality";
      /** Full engine shading CALIBRATION (strength slider + tuned constants + synthetic
       *  gradient/grain/sheen). Emitted even when shading is currently inactive (stage off, or
       *  strength 0) — the effective RENDER value is zeroed separately on `assets`. Dropping it
       *  while inactive lost the tuned calibration on the cloud's full-overwrite persist, so
       *  turning shading back on came up at the defaults (audit P2-1). */
      shading?: ShadingParams;
      /** MARKER keying colour (null = the engine's default magenta) + tolerance, "Protect
       *  product", and the product-profile CATEGORY. All three reshape the baked shell / print
       *  mask, so they must be persisted with the bake or the next reopen re-keys with the
       *  defaults and overwrites the good assets (audit P1-1 / P1-2 / P1-3). */
      markerColor?: [number, number, number] | null;
      markerTol?: number;
      shadedMagentaGuide?: boolean;
      protectProduct?: boolean;
      productCategory?: ProductCategory;
    };
  }): void;
  /** Layer refs from a previously-baked version — restored on open so a template built on
   *  another system shows its texture / clear-case-background layers + placement rotation here.
   *  `matteUrl` + the ai-edge flags restore the "background removed" state + calibration. */
  initialLayerRefs?: {
    textureUrl?: string; clearBgUrl?: string; textureBlend?: string; designRotation?: number;
    /** Saved "Texture / effect layer" enable flag — restores the layer even when it carries no
     *  texture image (UV varnish). Absent on records saved before this was persisted. */
    textureOn?: boolean;
    /** Saved "Show the clear case under the print area" flag, mapped from the EXISTING
     *  `clear_phone` ref (no new wire field needed — it always persisted, it was simply never
     *  read back). Same bug class as `textureOn`: the toggle was inferred from `clearBgUrl`,
     *  so enabling clear WITHOUT picking a background image was lost on reopen, and the next
     *  Save then wrote `clear_phone: false` to the cloud, making the loss permanent. */
    clearOn?: boolean;
    /** Saved per-layer opacities. These persisted and were honoured by the RENDER, but the
     *  editor always remounted at 100%, so reopening and saving silently overwrote a
     *  calibrated value with 1.0. */
    opacity?: { design?: number; texture?: number; phone?: number };
    matteUrl?: string; aiEdge?: boolean; aiEdgeKeep?: number;
    edgeFeather?: number; edgeChoke?: number; matteStrength?: number;
    /** Saved removal TECHNIQUE. `"chroma"` restores by re-running the magenta key on the
     *  pristine pixels — `matteUrl` is then never fetched (see `runMatteRestore`). */
    matteKind?: MatteKind;
    fillFit?: FitMode; designAspect?: [number, number] | null; varnish?: VarnishParams;
    printShadingStrength?: number; stageDespill?: boolean; stageRefine?: boolean;
    stageMatteStrength?: boolean; stageShading?: boolean; matteQuality?: "fast" | "quality";
    shading?: ShadingParams;
    /** Saved MARKER keying colour + tolerance, "Protect product", and the product-profile
     *  CATEGORY (audit P1-1 / P1-2 / P1-3). Absent on records saved before these were persisted,
     *  in which case the mount seeds stand (default magenta keying / protect on / baseType). */
    markerColor?: [number, number, number];
    shadedMagentaGuide?: boolean;
    markerTol?: number;
    protectProduct?: boolean;
    productCategory?: string;
  };
  /** Upload a CUSTOM (non-library) layer image to hosting (R2) and return its public URL,
   *  so it is NEVER kept device-local — the layer syncs to every system like a library pick.
   *  Returns null when hosting is unavailable (the layer then stays local, best-effort). */
  onUploadImage?: (bytes: Uint8Array) => Promise<string | null>;
  /** Case base type (spec 022 / #6): tpu | glass | … — seeds the product profile so the
   *  baked mockup reflects the material (glass → clear/glossy phone-behind-design). */
  baseType?: string | null;
  /** When true the version is locked (Active) — editing disabled (002 FR-003). */
  locked?: boolean;
  /** Called when the user requests a new version on a locked template. */
  onCreateNewVersion?(): void;
  width?: number;
  height?: number;
  /** Fired when the base image's natural pixel dimensions resolve — lets the parent size the
   *  default brand-logo rectangle (200×100px) in real image space. */
  onNaturalDims?(dims: { w: number; h: number }): void;
  /** WHICH RAIL SECTION IS OPEN, held by the page (fix round 1, F-5).
   *
   *  `TemplateEditor` renders this component under a `key` built from `forceSyncEpoch` and
   *  `prunedEpoch`, so a forced re-download or a view tombstone REMOUNTS it — and a `useState`
   *  in here goes with the mount, which is what the controlled pair was supposed to prevent and
   *  did not. The page renders the `key`, so the page is what outlives it.
   *
   *  Optional: a standalone caller (the suites, any embed with no epochs of its own) passes
   *  neither and the editor seeds its own key once, opening AI removal first. */
  railOpenKey?: string | null;
  onRailOpenChange?(key: string | null): void;
  /** WS-EXIF — the pixel size of the photo THIS view's stored geometry was drawn on
   *  (`WireView.base_image_size`), or null for a legacy view that never recorded one.
   *
   *  The editor compares it with the photo it actually decoded. A normalized polygon is bare
   *  0..1 numbers, so when the two frames are different shapes the stored quad draws rotated
   *  and oversized — the editor then REFUSES to draw it and offers a re-fit instead of showing
   *  the operator a wrong print area as if it were real. Null ⇒ draw as always. */
  authoringFrame?: { w: number; h: number } | null;
  /** Fired when the operator re-fits or accepts a mismatched print area, so the page can treat
   *  the current photo as this view's frame from now on (and stamp it on the next Save). */
  onAuthoringFrameResolved?(): void;
  /** WS-AUTOKEY (owner decision 1, 2026-09-24) — run the "Print guide key (no AI)" path by itself,
   *  ONCE per `caseImageUrl`, as soon as the image has decoded. The wizard sets it for a
   *  Higgsfield stage-2 hand-over, where the print area is painted `#FF00FF` by construction.
   *  It never starts the AI removal. Absent ⇒ the editor waits for a press, as it always has. */
  autoMagentaKey?: boolean;
  /** New shaded guides only; legacy/manual templates keep their existing keyer. */
  shadedMagentaGuide?: boolean;
  /** The automatic key's verdict: `"keyed"` (the print area is transparent and detected),
   *  `"not-found"` (the image has no magenta panel the keyer accepts) or `"off-coverage"` (it
   *  keyed, but outside the owner's 0.30–0.47 coverage band — `coverageCheck.ts`). Not fired
   *  when the key could not run at all (an unreadable canvas) — that is not a verdict. */
  onAutoMagentaKey?(result: AutoKeyVerdict, reason?: string): void;
  /** The case image could not be loaded or decoded. */
  onCaseImageError?(): void;
  /** The case job's product height the coverage band scales by (`productHeightPct`);
   *  `null` = no height restriction, absent = the 95 % default. */
  coverageHeightPct?: number | null;
  /** WS-AUTOKEY (owner decision 2) — which print-area light preset a NEW template starts on:
   * `"reference"` (the default) retains highlights/shadows for shaded guides and real photos.
   * Read once, as the seed; a saved calibration always wins. */
  initialPrintLight?: "flat" | "reference";
  /** WS-AUTOKEY (owner decision 3) — which rail this viewer gets.
   *
   *  - `"placement"` — a customer in the wizard: ONLY "Design fit & aspect" (fit, aspect, rotate,
   *    the test design and Preview mockup) and "Product". Every other section is not rendered. Its STATE still
   *    lives here at its default, so the bake still emits every key (auto marker, Flat, …) and a
   *    hidden setting is never a missing key on the cloud's full-overwrite persist.
   *  - `"basic"` — an admin on a template made from Generate: the customer's sections, then every
   *    other one (and `adminRailItems`) inside ONE "Advanced (admin)" section, closed at first.
   *  - `"advanced"` — an admin in the case flow's wizard: every section, Design fit first and
   *    open, the rest under one "Advanced" caption.
   *  - absent — every other host. The rail is exactly as it was. */
  railMode?: "placement" | "basic" | "advanced";
  /** The host's own sections for the "Advanced (admin)" section of `railMode="basic"`. */
  adminRailItems?: AccordionItem[];
  /** The host's camera shading fix, shown under the key controls (it is saved with the view). */
  cameraShading?: ReactNode;
}

/** How the current cutout was produced: the AI segmenter's grey matte, or the deterministic
 *  magenta chroma key (`transparent/keying.ts`). The two are NOT interchangeable — a chroma key
 *  carries its own despilled colours, so it is re-run rather than replayed. */
export type MatteKind = "ai" | "chroma";

/**
 * WHY a mockup compose is running — which decides whether it may write the status line.
 *
 * Only `"operator"` may. A compose that cannot run (no print area, no case image) has to say so,
 * or the button reads as doing nothing; but the other two reasons belong to the editor, not to a
 * question the operator asked, and a refusal message from them lands on top of the message the
 * operator IS reading.
 *
 * This used to be inferred from `uploadBake`, which is about UPLOADING and answers a different
 * question: `autoRemoveBg` persists a finished removal with `uploadBake: true`, so on a
 * marker-less template its bake replaced "Background removed (on-device AI)." with "No print
 * area yet — run Remove background (AI)…", telling the operator to run what they had just run.
 *
 *  - `"operator"` — the Preview button, `bakeNow` (Save), a layer or test-design pick. Speaks.
 *  - `"live"`     — the 320 ms debounced appearance pass. Fires on every slider notch and on
 *                   mount; silent.
 *  - `"persist"`  — the bake `autoRemoveBg` fires to save the matte it just produced. The removal
 *                   owns the status line; silent.
 */
export type ComposeReason = "operator" | "live" | "persist";

/** A centered default brand-logo rectangle sized ~200×100px in the base image's pixel space,
 *  expressed in normalized (0..1) coords. Falls back to a proportional box when the base image
 *  dimensions aren't known yet. Returned as [TL, TR, BR, BL]. */
export function defaultBrandRect(dims?: { w: number; h: number } | null): Polygon {
  const w = dims?.w && dims.w > 0 ? dims.w : 1000;
  const h = dims?.h && dims.h > 0 ? dims.h : 2000;
  const halfW = Math.min(0.45, 100 / w); // half of 200px
  const halfH = Math.min(0.45, 50 / h); // half of 100px
  return rectFromDiagonal({ x: 0.5 - halfW, y: 0.5 - halfH }, { x: 0.5 + halfW, y: 0.5 + halfH });
}

export type AutoKeyVerdict = "keyed" | "not-found" | "off-coverage";
